import { fillingStatusFromValue } from 'components/utils/fillingType';
import Papa from 'papaparse';
import { typeFromValue } from 'components/utils/containerType';
import { getDisplayedAPDifference } from './apDifference';
import { getColumnMappings } from './mappingUtils';
import { getFullCapacity } from './fullVolume';

const getCsvName = (mappings, key) => mappings?.[key]?.csvName;
const getUnit = (mappings, key) => mappings?.[key]?.unit;

const getContainerReference = (item, inventory) => {
  const columnMappings = getColumnMappings(inventory);
  const ivtContainerIdMapping = getCsvName(columnMappings, 'ivtContainerId');
  if (Array.isArray(ivtContainerIdMapping)) {
    return ivtContainerIdMapping.map((colName) => item[colName]).join(' / ');
  }
  return ivtContainerIdMapping ? item[ivtContainerIdMapping] : '';
};

// Data required to calculate a row, as codes: { code, params }. Fields are given as
// { key, csv }: csv is the inventory column, absent when the column is not configured.
// Shared by the calculation, the issues table and the "to calculate" count.
export const getCalculationReadiness = (container, columnMappings) => {
  const issues = [];
  const fieldOf = (key) => ({ key, csv: getCsvName(columnMappings, key) });
  const missing = (key) => issues.push({ code: 'field_invalid_or_missing', params: { field: fieldOf(key) } });
  const fillingCsv = getCsvName(columnMappings, 'filling');
  if (!fillingCsv) {
    missing('filling');
    return { ready: false, issues };
  }
  const fillingStatus = fillingStatusFromValue(container[fillingCsv]);

  const liquidVolumeCsv = getCsvName(columnMappings, 'liquidVolume');
  const liquidVolumeDirectlyProvided = liquidVolumeCsv ? !!container[liquidVolumeCsv] : false;

  const required = new Set();
  const require = (key) => required.add(key);

  // Determine container type if available
  const typeCsv = getCsvName(columnMappings, 'type');
  const containerType = typeCsv ? typeFromValue(String(container[typeCsv] ?? '')) : null;

  if (containerType === 'weighed-container') {
    // For weighed containers, require these four fields
    ['realTAV', 'containerMass', 'mass', 'volumetricMass'].forEach(require);
  } else {
    // For other types, always need abv/tav + temps for conversions (barrel/volume types)
    ['tav', 'tavTemperature', 'volumeTemperature'].forEach(require);

    if (fillingStatus === 'full') {
      const capacity = getFullCapacity(container, columnMappings);
      if (capacity.value === null) {
        const usable = capacity.field === 'usableCapacity'
          || capacity.field === getCsvName(columnMappings, 'usableCapacity');
        missing(usable ? 'usableCapacity' : 'totalCapacity');
      }
    } else if (!liquidVolumeDirectlyProvided && containerType === 'volume') {
      // A direct-volume container is only calculated from its measured volume.
      issues.push({ code: 'needs_liquid_volume', params: { field: fieldOf('liquidVolume') } });
    } else if (!liquidVolumeDirectlyProvided) {
      require('totalCapacity');
      require('heightUnderBung');
      if (containerType === 'truncated-cone') {
        require('internalDiameterDown');
        require('internalDiameterUp');
      }
      const filled = (key) => {
        const csv = getCsvName(columnMappings, key);
        return csv ? !!container[csv] : false;
      };
      if (!filled('emptyHeight') && !filled('liquidHeight')) {
        issues.push({
          code: 'needs_volume_calculation_field',
          params: { fields: ['liquidHeight', 'emptyHeight', 'liquidVolume'].map(fieldOf) },
        });
      }
    }
  }

  required.forEach((key) => {
    const csv = getCsvName(columnMappings, key);
    if (!csv || container[csv] === undefined || container[csv] === null || container[csv] === '') {
      missing(key);
    }
  });
  return { ready: issues.length === 0, issues };
};

// Translated message of a readiness issue. A configured column is named as in the
// inventory; a column that is not configured gets its translated label.
export const readinessMessage = ({ code, params }, t) => {
  const label = ({ key, csv }) => csv
    || `${t(`barrel_inventory.column_options.${key}`)} (${t('inventory_issues.column_not_configured')})`;
  if (code === 'needs_volume_calculation_field') {
    // Only configured columns can be filled in; list them when there are some.
    const configured = params.fields.filter((field) => field.csv);
    return t(code, { fields: (configured.length ? configured : params.fields).map(label).join(', ') });
  }
  return t(code, { field: label(params.field) });
};

export const isContainerDataReady = (container, inventory, t) => {
  const { ready, issues } = getCalculationReadiness(container, getColumnMappings(inventory));
  return {
    isReady: ready,
    errors: issues.map((issue) => readinessMessage(issue, t)),
  };
};

export const parseNumberOrEmptyString = (value) => (value ? Number(value) : '');

export const parseNumberOrNull = (value) => (value ? Number(value) : null);

export const mergeConversionResults = (arrayToUpdate, conversionResultsCsv, rowIds, t, inventory) => {
  const conversionResults = Papa.parse(conversionResultsCsv, {
    header: true,
    dynamicTyping: false,
    skipEmptyLines: true,
    delimitersToGuess: [';', ','],
  }).data;

  // Create a map for easy access to conversion results by reference
  const conversionResultsMap = conversionResults.reduce((acc, result) => {
    acc[result.reference] = result;
    return acc;
  }, {});

  const columnMappings = getColumnMappings(inventory);
  const containersWithErrors = [];

  const updatedContainers = arrayToUpdate.map((container, index) => {
    const result = conversionResultsMap[getContainerReference(container, inventory)];
    if (result) {
      // Check if this container had an error
      if (result.error && result.error.trim() !== '') {
        containersWithErrors.push({
          reference: result.reference,
          error: result.error,
          lineNumber: index + 1, // Line numbers are 1-based for display
        });
      }

      return {
        ...container,
        [getCsvName(columnMappings, 'ABVAt20cResult')]:
          parseNumberOrEmptyString(result.ABVAt20c),
        [getCsvName(columnMappings, 'pureAlcoholVolumeAt20cResult')]:
          parseNumberOrEmptyString(result.pureAlcoholVolumeAt20c),
        [getCsvName(columnMappings, 'liquidVolumeResult')]:
          parseNumberOrEmptyString(result.liquidVolume),
        [getCsvName(columnMappings, 'volumeAt20cResult')]:
          parseNumberOrEmptyString(result.volumeAt20c),
        [getCsvName(columnMappings, 'volumeCorrectionFactorResult')]:
          parseNumberOrEmptyString(result.volumeCorrectionFactor),
        [t('create_inventory.default_mapping_fields.volumeUnit')]:
          result.volumeUnit,
      };
    }
    return container;
  });

  return {
    updatedContainers,
    containersWithErrors,
    returnedReferences: new Set(conversionResults.map((result) => String(result.reference))),
  };
};

export const calculateEvaporationForContainer = (container, columnMappings) => {
  const pureAlcoholTheoreticalCsv = columnMappings.pureAlcoholTheoretical?.csvName;
  const pureAlcoholVolumeAt20cResultCsv = columnMappings.pureAlcoholVolumeAt20cResult?.csvName;
  const evaporationCsv = columnMappings.evaporation?.csvName;
  const percentEvaporationCsv = columnMappings.percentEvaporation?.csvName;

  // Early return if required mappings are missing
  if (!pureAlcoholTheoreticalCsv || !pureAlcoholVolumeAt20cResultCsv) {
    return container;
  }

  // Early return if evaporation fields are not mapped (optional fields)
  if (!evaporationCsv || !percentEvaporationCsv) {
    return container;
  }

  const newContainer = { ...container };

  // Keep stored results aligned with the values derived for old inventories in the grid/export.
  newContainer[evaporationCsv] = getDisplayedAPDifference(container, columnMappings, 'evaporation');
  newContainer[percentEvaporationCsv] = getDisplayedAPDifference(container, columnMappings, 'percentEvaporation');
  return newContainer;
};

function setEmptyConversionResultsToZero(container, columnMappings, t) {
  const abvAt20Csv = getCsvName(columnMappings, 'ABVAt20cResult');
  const pureAPCsv = getCsvName(columnMappings, 'pureAlcoholVolumeAt20cResult');
  const liquidVolumeResCsv = getCsvName(columnMappings, 'liquidVolumeResult');
  const volumeAt20Csv = getCsvName(columnMappings, 'volumeAt20cResult');
  const volumeCorrectionFactorCsv = getCsvName(columnMappings, 'volumeCorrectionFactorResult');
  const columns = [
    abvAt20Csv,
    pureAPCsv,
    liquidVolumeResCsv,
    volumeAt20Csv,
    volumeCorrectionFactorCsv,
  ].filter(Boolean);

  const volumeUnit = getUnit(columnMappings, 'volumeAt20cResult');

  const updatedContainer = { ...container };

  columns.forEach((column) => {
    if (column) {
      updatedContainer[column] = 0;
    }
  });

  const volumeUnitColName = t('create_inventory.default_mapping_fields.volumeUnit');

  updatedContainer[volumeUnitColName] = volumeUnit;

  return updatedContainer;
}

export function updateContainersArrayWithEmptyContainers(containersArray, selectedEmptyContainers, columnMappings, t) {
  const newArray = containersArray.map((container) => {
    const selectedContainer = selectedEmptyContainers.find((sc) => sc.id === container.id);
    if (selectedContainer) {
      return setEmptyConversionResultsToZero(container, columnMappings, t);
    }
    return container;
  });

  return newArray;
}
